import React, { useState } from 'react';
import { useMutation } from '@common/hooks';
import { Dropdown, MenuProps } from 'antd';
import { createMessage } from 'works/legacy/lib/message-helper';
import { PerformStaffingRequestAction } from '@applicant-flow/graphql/mutations';
import { StaffingRequestStateChangeModal } from 'works/legacy/components/staffing-request-state-change-modal';
import { StateMenuInfo } from 'works/legacy/components/state-menu-info';
import { StaffingRequestStateBadge } from 'works/legacy/components/staffing-request-state-badge/staffing-request-state-badge';
import { StaffingRequestType } from '@applicant-flow/types';

const ACCEPTABLE_STATES = ['ON_HOLD', 'OPEN', 'PENDING'];

export function StateMenu({
  staffingRequest,
  viewMode,
}: {
  staffingRequest: StaffingRequestType;
  viewMode: string;
}) {
  const [targetState, setTargetState] = useState<string>();
  const [onStateModalVisible, setStateModalVisible] = useState(false);

  const onClick = (action: string) => {
    if (action === 'hold' || action === 'cancel') return;

    performAction({
      variables: {
        staffingRequestId: staffingRequest.id,
        input: {
          action: action,
        },
      },
    });
  };

  const onClickWithConfirmation = (action: string) => {
    setTargetState(action);
    setStateModalVisible(true);
  };

  const [performAction] = useMutation(PerformStaffingRequestAction, {
    onCompleted: () => {
      createMessage('success', 'Staffing Request status successfully changed');
    },
    onError: () => {
      createMessage('error', 'Failed to update staffing request status');
    },
  });

  const onConfirm = () => {
    const action = {
      ON_HOLD: 'hold',
      CANCELLED: 'cancel',
    }[targetState as string];

    performAction({
      variables: {
        staffingRequestId: staffingRequest.id,
        input: {
          action: action,
        },
      },
    });
    setStateModalVisible(false);
  };

  const onCancel = () => {
    setStateModalVisible(false);
    setTargetState(undefined);
  };

  const menuDropdownActive = () => {
    if (!ACCEPTABLE_STATES.includes(staffingRequest.state)) return false;
    return staffingRequest.canUpdate.value;
  };

  const items: MenuProps['items'] = [
    staffingRequest.canOpenUp.value && staffingRequest.state !== 'OPEN'
      ? {
          key: 'open_up',
          label: 'Open',
          onClick: (e) => {
            e.domEvent.stopPropagation();
            onClick('open_up');
          },
        }
      : null,
    staffingRequest.canHold.value && staffingRequest.state !== 'ON_HOLD'
      ? {
          key: 'hold',
          label: 'On Hold',
          onClick: (e) => {
            e.domEvent.stopPropagation();
            onClickWithConfirmation('ON_HOLD');
          },
        }
      : null,
    staffingRequest.canCancel.value
      ? {
          key: 'cancel',
          label: 'Cancel',
          onClick: (e) => {
            e.domEvent.stopPropagation();
            onClickWithConfirmation('CANCELLED');
          },
        }
      : null,
  ];

  return (
    <>
      {(menuDropdownActive() && (
        <div className="state-menu-dropdown">
          <Dropdown menu={{ items }}>
            <a>
              <StateMenuInfo
                state={staffingRequest.state}
                timeStamp={
                  viewMode !== 'list'
                    ? staffingRequest.stateChangedAt
                    : undefined
                }
                submissionsPaused={staffingRequest.submissionsPaused}
              />
            </a>
          </Dropdown>
        </div>
      )) || (
        <StaffingRequestStateBadge
          state={staffingRequest.state}
          timestamp={
            viewMode !== 'list' ? staffingRequest.stateChangedAt : undefined
          }
          submissionsPaused={staffingRequest.submissionsPaused}
        />
      )}

      {onStateModalVisible && (
        <StaffingRequestStateChangeModal
          staffingRequestId={staffingRequest.id}
          visible={onStateModalVisible}
          targetState={targetState as string}
          onOk={(e) => {
            e.stopPropagation();
            onConfirm();
          }}
          onCancel={(e) => {
            e.stopPropagation();
            onCancel();
          }}
        />
      )}
      <style jsx>{`
        .state-menu-dropdown :global(.ant-dropdown-trigger) {
          display: flex;
          flex-wrap: wrap;
        }
      `}</style>
    </>
  );
}
